import { useRef, ReactNode, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import { User } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { MessageShareMenu } from "./MessageShareMenu";
import { useIsMobile } from "@/hooks/use-mobile";

interface MessageItemProps {
  content: string | ReactNode;
  role: "assistant" | "user";
  userQuestion?: string;
}

export const MessageItem = ({ content, role, userQuestion = '' }: MessageItemProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const isAssistant = role === "assistant";
  const isMobile = useIsMobile();
  const [userInitials, setUserInitials] = useState("U");
  
  // Get user initials from Supabase auth - same approach as sidebar
  useEffect(() => {
    if (role === "user") {
      async function getUserInitials() {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          const email = user.email;
          const parts = email.split('@')[0].split('.');
          const initials = parts.map(part => part[0].toUpperCase()).join('');
          setUserInitials(initials.substring(0, 2));
        }
      }
      getUserInitials();
    }
  }, [role]);

  // If content is already a React element (e.g., LoadingIndicator), render it directly
  if (typeof content !== 'string') {
    return (
      <div className={cn(
        "w-full py-3 md:py-4 group mobile-safe",
        role === "assistant" ? "bg-[#0c1221]/80 backdrop-blur-sm" : "bg-transparent"
      )}>
        <div className={cn(
          "max-w-4xl mx-auto w-full flex mobile-chat-container",
          isMobile ? "flex-col items-center px-4" : "flex-row px-4 md:px-6"
        )}>
          <div className={cn(
            "shrink-0 flex items-center justify-center",
            role === "assistant" ? "text-indigo-500" : "text-white/80",
            isMobile ? "mb-1.5 w-full" : "mr-3"
          )}>
            {/* Avatar/Icon */}
            {role === "assistant" ? (
              <div className="flex items-center gap-2">
                <div className={cn(
                  "rounded-full flex items-center justify-center overflow-hidden border border-indigo-500/40 shadow-sm shadow-indigo-500/20",
                  isMobile ? "h-8 w-8" : "h-7 w-7 md:h-8 md:w-8"
                )}>
                  <img 
                    src="/strykr-logo.jpeg" 
                    alt="Strykr AI" 
                    className="h-full w-full object-cover"
                  />
                </div>
                {isMobile && (
                  <span className="text-indigo-400 font-medium text-sm">Strykr AI</span>
                )}
              </div>
            ) : (
              <Avatar className={cn(
                "border border-indigo-500/30 shadow-sm shadow-indigo-500/10",
                isMobile ? "h-8 w-8" : "h-7 w-7 md:h-8 md:w-8"
              )}>
                <AvatarFallback className={cn(
                  "bg-gradient-to-br from-blue-900 to-indigo-900 text-white font-medium",
                  isMobile ? "text-sm" : "text-xs"
                )}>{userInitials}</AvatarFallback>
              </Avatar>
            )}
          </div>
          <div className={cn(
            "min-w-0 overflow-visible mobile-chat-text", 
            isMobile ? "w-full px-2" : "flex-1"
          )} ref={contentRef}>
            {content}
          </div>
        </div>
      </div>
    );
  }

  const MessageContent = () => {
    if (role === "user") {
      return (
        <div className={cn(
          "text-white font-medium py-1",
          "mobile-chat-text user-message",
          isMobile ? "text-base" : "text-base leading-normal"
        )}>
          {content}
        </div>
      );
    }

    // Split content into sections by headers for better rendering
    const sections = (content as string).split(/(?=^#+ |\n#+ )/gm);
    
    return (
      <div className="space-y-6 md:space-y-8 w-full overflow-visible">
        {sections.map((section, index) => {
          if (!section.trim()) return null;

          return (
            <div key={index} className="prose prose-invert max-w-none break-words overflow-visible overflow-x-auto">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1 className={cn(
                      "font-bold text-indigo-400 border-b border-indigo-500/20",
                      isMobile 
                        ? "text-xl mb-4 mt-6 pb-2 text-center" 
                        : "text-2xl md:text-3xl mb-4 md:mb-6 mt-6 md:mt-8 pb-2 md:pb-3 text-center"
                    )}>{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className={cn(
                      "font-semibold text-indigo-400 border-b border-indigo-500/10",
                      isMobile 
                        ? "text-lg mb-3 mt-5 pb-1 text-center" 
                        : "text-xl md:text-2xl mb-3 md:mb-4 mt-5 md:mt-6 pb-1 md:pb-2 text-center"
                    )}>{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className={cn(
                      "font-medium text-indigo-400",
                      isMobile 
                        ? "text-base mb-2 mt-4" 
                        : "text-lg md:text-xl mb-2 md:mb-3 mt-4 md:mt-5"
                    )}>{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className={cn(
                      "text-white/90 text-left leading-relaxed",
                      isMobile 
                        ? "text-base mb-4" 
                        : "text-base md:text-lg mb-4 md:mb-5"
                    )}>{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className={cn(
                      "text-left pl-0",
                      isMobile 
                        ? "space-y-3 mb-5" 
                        : "space-y-2 md:space-y-3 mb-4 md:mb-6"
                    )}>{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className={cn(
                      "list-decimal text-left",
                      isMobile 
                        ? "pl-4 space-y-3 mb-5" 
                        : "pl-5 md:pl-6 space-y-2 md:space-y-3 mb-4 md:mb-6"
                    )}>{children}</ol>
                  ),
                  li: ({ children }) => (
                    <li className="flex items-start text-white/90 text-left">
                      <span className={cn(
                        "text-indigo-400 mt-1 flex-shrink-0",
                        isMobile 
                          ? "mr-3 text-sm" 
                          : "mr-3 md:mr-4 text-sm md:text-base"
                      )}>•</span>
                      <span className={cn(
                        "flex-1 overflow-visible leading-relaxed",
                        isMobile 
                          ? "text-base" 
                          : "text-base md:text-lg"
                      )}>{children}</span>
                    </li>
                  ),
                  code: ({ className, children }) => (
                    className?.includes('inline') ? 
                      <code className={cn(
                        "px-2 py-1 bg-[#162038] rounded font-mono text-indigo-300",
                        isMobile ? "text-sm" : "text-sm md:text-base"
                      )}>
                        {children}
                      </code>
                      :
                      <pre className="p-4 md:p-5 bg-[#0c1221] rounded-lg overflow-x-auto border border-indigo-500/20 shadow-sm my-4 md:my-5 max-w-full">
                        <code className={cn(
                          "font-mono text-indigo-300 whitespace-pre-wrap break-all",
                          isMobile ? "text-sm leading-relaxed" : "text-sm md:text-base"
                        )}>{children}</code>
                      </pre>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-white">{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic text-white/85">{children}</em>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className={cn(
                      "border-l-4 border-indigo-400/50 italic text-white/80 py-2",
                      isMobile 
                        ? "pl-4 my-4" 
                        : "pl-4 md:pl-5 my-4 md:my-5"
                    )}>
                      {children}
                    </blockquote>
                  ),
                  a: ({ children, href }) => (
                    <a 
                      href={href} 
                      className="text-indigo-400 hover:text-purple-300 underline transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto mb-4 md:mb-5 rounded-lg border border-indigo-500/20">
                      <table className="min-w-full divide-y divide-gray-700 text-left">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-[#162038]">{children}</thead>
                  ),
                  tbody: ({ children }) => (
                    <tbody className="divide-y divide-gray-700">{children}</tbody>
                  ),
                  th: ({ children }) => (
                    <th className={cn(
                      "px-4 md:px-5 py-3 md:py-4 font-medium text-indigo-300 text-left",
                      isMobile ? "text-sm" : "text-sm md:text-base"
                    )}>{children}</th>
                  ),
                  td: ({ children }) => (
                    <td className={cn(
                      "px-4 md:px-5 py-3 md:py-4 text-white/80 text-left",
                      isMobile ? "text-sm" : "text-sm md:text-base"
                    )}>{children}</td>
                  ),
                }}
              >
                {section}
              </ReactMarkdown>
            </div>
          );
        })}
        {isAssistant && content === "" && (
          <span className="inline-block h-4 w-2 ml-1 bg-indigo-400 animate-pulse"></span>
        )}
        
        {/* Q&A Share Menu - appears directly after content for assistant messages */}
        {role === 'assistant' && typeof content === 'string' && content.trim() !== '' && userQuestion.trim() !== '' && (
          <div className="flex justify-start mt-4 md:mt-5 mb-2 md:mb-3">
            <MessageShareMenu 
              userMessage={userQuestion}
              assistantMessage={content}
            />
          </div>
        )}
      </div>
    );
  };

  // Don't render empty assistant messages - these will be handled by the centered greeting in MessageList
  if (role === "assistant" && typeof content === "string" && content.trim() === "") {
    return null;
  }
  
  return (
    <div className={cn(
      "w-full border-b border-white/5 group mobile-safe",
      role === "assistant" ? "bg-[#0c1221]/80 backdrop-blur-sm" : "bg-black/5",
      isMobile ? "py-4 px-1" : "py-5"
    )}>
      <div className={cn(
        "max-w-4xl mx-auto w-full flex mobile-chat-container",
        isMobile ? "flex-col items-center px-4" : "flex-row px-4 md:px-6"
      )}>
        <div className={cn(
          "shrink-0 flex items-center justify-center",
          role === "assistant" ? "text-indigo-500" : "text-white/80",
          isMobile ? "mb-1.5 w-full" : "mr-3"
        )}>
          {/* Avatar/Icon */}
          {role === "assistant" ? (
            <div className="flex items-center gap-2">
              <div className={cn(
                "rounded-full flex items-center justify-center overflow-hidden border border-indigo-500/40 shadow-sm shadow-indigo-500/20",
                isMobile ? "h-8 w-8" : "h-7 w-7 md:h-8 md:w-8"
              )}>
                <img 
                  src="/strykr-logo.jpeg" 
                  alt="Strykr AI" 
                  className="h-full w-full object-cover"
                />
              </div>
              {isMobile && (
                <span className="text-indigo-400 font-medium text-sm">Strykr AI</span>
              )}
            </div>
          ) : (
            <Avatar className={cn(
              "border border-indigo-500/30 shadow-sm shadow-indigo-500/10",
              isMobile ? "h-8 w-8" : "h-7 w-7 md:h-8 md:w-8"
            )}>
              <AvatarFallback className={cn(
                "bg-gradient-to-br from-blue-900 to-indigo-900 text-white font-medium",
                isMobile ? "text-sm" : "text-xs"
              )}>{userInitials}</AvatarFallback>
            </Avatar>
          )}
        </div>
        <div className={cn(
          "min-w-0 overflow-visible mobile-chat-text", 
          isMobile ? "w-full px-2" : "flex-1"
        )} ref={contentRef}>
          <MessageContent />
        </div>
      </div>
    </div>
  );
};
